import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { W, H, project, MAJOR_CITIES } from "./constants";
import { api } from "../../lib/api";

/**
 * Live hub pulses on the world map.
 *
 * - Renders a soft purple pulse on top of any city that has active chat activity.
 * - On hover, fetches the last 3 messages from that hub and shows a "peek" tooltip.
 * - Click navigates to the full chat room.
 */
export default function HubPulses({ hubs, viewX, viewY, viewW, viewH }) {
  const nav = useNavigate();
  const [hoverCity, setHoverCity] = useState(null); // {name, x, y, messages, loading}
  const cacheRef = useRef(new Map()); // { cityKey: {ts, messages} } — 30s TTL
  const refreshRef = useRef(null);
  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  useEffect(() => () => {
    if (refreshRef.current) clearInterval(refreshRef.current);
  }, []);

  const pulses = hubs
    .map((h) => {
      const match = MAJOR_CITIES.find((mc) => mc[0].toLowerCase() === h.city.toLowerCase());
      if (!match) return null;
      const [name, la, ln] = match;
      const [x, y] = project(la, ln);
      return { name, x, y, city: h.city, message_count: h.message_count };
    })
    .filter(Boolean);

  const visiblePulses = useMemo(
    () => pulses.filter((p) => p.x >= viewX && p.x <= viewX + viewW && p.y >= viewY && p.y <= viewY + viewH),
    [pulses, viewX, viewY, viewW, viewH],
  );

  const fetchPreview = async (cityKey, force = false) => {
    const cached = cacheRef.current.get(cityKey);
    if (!force && cached && Date.now() - cached.ts < 30_000) {
      return cached.messages;
    }
    try {
      const r = await api.get(`/hubs/${encodeURIComponent(cityKey)}/messages`);
      const messages = (r.data || []).slice(-3); // last 3
      cacheRef.current.set(cityKey, { ts: Date.now(), messages });
      return messages;
    } catch (e) {
      return cached?.messages || [];
    }
  };

  const stopRefresh = () => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    refreshRef.current = null;
  };

  // Tooltip appears instantly (cached or "loading"), then keeps refreshing every 3s while open.
  const showPeek = (p) => {
    stopRefresh();
    const key = p.city.toLowerCase();
    const cached = cacheRef.current.get(key);
    setHoverCity({ ...p, messages: cached?.messages ?? null, loading: !cached });
    const load = async (force) => {
      const messages = await fetchPreview(key, force);
      setHoverCity((cur) => (cur && cur.name === p.name ? { ...cur, messages, loading: false } : cur));
    };
    load(false);
    refreshRef.current = setInterval(() => load(true), 3000);
  };
  const handleHoverEnter = (p) => { if (!isTouch) showPeek(p); };
  const handleHoverLeave = () => {
    if (isTouch) return;
    stopRefresh();
    setHoverCity(null);
  };
  const openHub = (city) => nav(`/chat/hub/${encodeURIComponent(city.toLowerCase())}`);
  // Touch: first tap peeks, second tap on the same pulse opens the hub.
  const handleClick = (p) => {
    if (isTouch && (!hoverCity || hoverCity.name !== p.name)) { showPeek(p); return; }
    stopRefresh();
    openHub(p.city);
  };

  const inView = (x, y) => x >= viewX && x <= viewX + viewW && y >= viewY && y <= viewY + viewH;
  const zoomK = W / viewW;
  const pulseR = zoomK > 2 ? 3 / zoomK : 3;
  const strokeW = zoomK > 2 ? 1.5 / zoomK : 1.5;

  return (
    <>
      <g data-testid="hub-pulses-layer">
        {visiblePulses.map((p) => (
          <g
            key={`hub-${p.name}`}
            data-testid={`hub-pulse-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            onMouseEnter={() => handleHoverEnter(p)}
            onMouseLeave={handleHoverLeave}
            onClick={() => handleClick(p)}
            style={{ cursor: "pointer" }}
          >
            {/* Wide hitbox so hover is easy at any zoom */}
            <circle cx={p.x} cy={p.y} r={Math.max(8, 10 / zoomK)} fill="transparent" pointerEvents="all" />
            <circle cx={p.x} cy={p.y} r={pulseR + 1} fill="none" stroke="#8a3fdc" strokeWidth={strokeW} strokeOpacity="0.9" pointerEvents="none">
              <animate attributeName="r" values={`${pulseR};${pulseR * 4};${pulseR}`} dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.9;0;0.9" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx={p.x} cy={p.y} r={pulseR} fill="#8a3fdc" opacity="0.9" pointerEvents="none" />
          </g>
        ))}
      </g>

      {/* Hover preview tooltip — rendered inside SVG in viewbox coords so it moves with pan/zoom */}
      {hoverCity && inView(hoverCity.x, hoverCity.y) && (() => {
        // All tooltip geometry is in viewBox units, so scale with zoom to keep a constant on-screen size.
        const k = viewW / W;
        const tooltipW = 200 * k;
        const tooltipH = 88 * k;
        const pad = 8 * k;
        const flip = hoverCity.x + tooltipW + 12 * k > viewX + viewW;
        const tx = flip ? hoverCity.x - tooltipW - 12 * k : hoverCity.x + 12 * k;
        const ty = Math.max(viewY + 4 * k, Math.min(viewY + viewH - tooltipH - 4 * k, hoverCity.y - tooltipH / 2));
        const fs = 8 * k;
        return (
          <g data-testid={`hub-preview-${hoverCity.name.toLowerCase().replace(/\s+/g, "-")}`} pointerEvents="none">
            <rect x={tx} y={ty} width={tooltipW} height={tooltipH} rx={4 * k} fill="#15111A" fillOpacity="0.95" stroke="#8a3fdc" strokeWidth={0.6 * k} />
            <text x={tx + pad} y={ty + fs + 4 * k} fill="#C8A96E" fontSize={fs} fontFamily="'Courier Prime', monospace" style={{ textTransform: "uppercase", letterSpacing: `${0.8 * k}px` }}>
              {hoverCity.name} · Live Hub
            </text>
            {hoverCity.loading && (
              <text x={tx + pad} y={ty + fs * 2 + 12 * k} fill="#A89C98" fontSize={fs}>Loading peeks…</text>
            )}
            {hoverCity.messages && hoverCity.messages.length === 0 && (
              <text x={tx + pad} y={ty + fs * 2 + 12 * k} fill="#A89C98" fontSize={fs}>Quiet now — be the first.</text>
            )}
            {hoverCity.messages && hoverCity.messages.length > 0 && (
              <g>
                {hoverCity.messages.slice(0, 3).map((m, i) => {
                  const raw = (m.text || "").slice(0, 32);
                  const line = m.user_name ? `${m.user_name}: ${raw}` : raw;
                  return (
                    <text key={m.message_id || `${m.user_name}-${m.created_at}`} x={tx + pad} y={ty + fs * 2 + 12 * k + i * (fs + 3 * k)} fill="#E8DFD3" fontSize={fs} fontFamily="'Courier Prime', monospace">
                      {line.length >= 40 ? line + "…" : line}
                    </text>
                  );
                })}
              </g>
            )}
            <text x={tx + tooltipW - pad} y={ty + tooltipH - 6 * k} fill="#C8A96E" fillOpacity="0.7" fontSize={fs * 0.875} textAnchor="end" style={{ textTransform: "uppercase", letterSpacing: `${k}px` }}>
              {isTouch ? "Tap again to open →" : "Click to open →"}
            </text>
          </g>
        );
      })()}
    </>
  );
}
